"use client"

// Minimal, dependency-free markdown renderer for the personalized setup guide.
// The guide markdown is constrained (we control the template + the LLM intro),
// so a hand-rolled pass covering headings, bold/italic, inline code, links,
// lists, blockquotes, tables, and rules is enough — no CommonMark dep.
//
// Themed via CSS custom properties on the wrapper. Venture passes its warm
// paper palette; GTM passes its --gtm-* tokens. Anything not overridden falls
// back to a neutral default that reads on both light and dark backgrounds.

import React from "react"

export interface GuideTheme {
  text?: string
  textMuted?: string
  heading?: string
  headingFont?: string
  accent?: string
  line?: string
  cardBg?: string
  quoteBg?: string
  quoteBorder?: string
  codeBg?: string
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

function renderInline(s: string): string {
  let t = escapeHtml(s)
  t = t.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
  t = t.replace(/(^|[^*])\*([^*]+)\*(?!\*)/g, "$1<em>$2</em>")
  t = t.replace(/`([^`]+)`/g, '<code class="sg-md-code">$1</code>')
  t = t.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="sg-md-link">$1</a>',
  )
  return t
}

interface Block { type: string; content: string[] }

function blocksOf(md: string): Block[] {
  const lines = md.replace(/\r\n/g, "\n").split("\n")
  const blocks: Block[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    if (!line.trim()) { i++; continue }
    if (/^---+\s*$/.test(line)) { blocks.push({ type: "hr", content: [] }); i++; continue }
    if (/^#{1,6}\s+/.test(line)) { blocks.push({ type: "heading", content: [line] }); i++; continue }
    if (/^>\s?/.test(line)) {
      const buf: string[] = []
      while (i < lines.length && /^>\s?/.test(lines[i])) { buf.push(lines[i].replace(/^>\s?/, "")); i++ }
      blocks.push({ type: "blockquote", content: buf }); continue
    }
    if (/^\|.*\|/.test(line)) {
      const buf: string[] = []
      while (i < lines.length && /^\|.*\|/.test(lines[i])) { buf.push(lines[i]); i++ }
      blocks.push({ type: "table", content: buf }); continue
    }
    if (/^(\d+\.|[-*])\s+/.test(line)) {
      const buf: string[] = []
      while (i < lines.length && (/^(\d+\.|[-*])\s+/.test(lines[i]) || /^\s{2,}\S/.test(lines[i]))) { buf.push(lines[i]); i++ }
      blocks.push({ type: "list", content: buf }); continue
    }
    const buf: string[] = []
    while (
      i < lines.length && lines[i].trim() &&
      !/^#{1,6}\s+/.test(lines[i]) && !/^>\s?/.test(lines[i]) &&
      !/^\|.*\|/.test(lines[i]) && !/^(\d+\.|[-*])\s+/.test(lines[i]) && !/^---+\s*$/.test(lines[i])
    ) { buf.push(lines[i]); i++ }
    blocks.push({ type: "paragraph", content: buf })
  }
  return blocks
}

// A list block may contain nested (indented) sub-items. Render one flat level
// plus a single nested level — enough for the guide's structure.
function renderList(content: string[], key: number): React.ReactElement {
  const ordered = /^\d+\./.test(content[0])
  type Item = { text: string; children: string[] }
  const items: Item[] = []
  let cur: Item | null = null
  for (const raw of content) {
    const indented = /^\s{2,}\S/.test(raw)
    if (indented && cur) {
      cur.children.push(raw.replace(/^\s+/, ""))
    } else if (/^(\d+\.|[-*])\s+/.test(raw)) {
      if (cur) items.push(cur)
      cur = { text: raw.replace(/^(\d+\.|[-*])\s+/, ""), children: [] }
    } else if (cur) {
      cur.text += " " + raw.trim()
    }
  }
  if (cur) items.push(cur)

  const Tag = ordered ? "ol" : "ul"
  return React.createElement(
    Tag,
    { key, className: ordered ? "sg-md-ol" : "sg-md-ul" },
    items.map((it, i) => (
      <li key={i}>
        <span dangerouslySetInnerHTML={{ __html: renderInline(it.text) }} />
        {it.children.length > 0 && (
          <ul className="sg-md-ul sg-md-ul-nested">
            {it.children
              .filter((c) => /^(\d+\.|[-*])\s+/.test(c))
              .map((c, ci) => (
                <li key={ci} dangerouslySetInnerHTML={{ __html: renderInline(c.replace(/^(\d+\.|[-*])\s+/, "")) }} />
              ))}
          </ul>
        )}
      </li>
    )),
  )
}

function renderBlock(b: Block, key: number): React.ReactElement {
  if (b.type === "hr") return <hr key={key} className="sg-md-hr" />
  if (b.type === "heading") {
    const m = b.content[0].match(/^(#{1,6})\s+(.*)$/)!
    const level = m[1].length
    const Tag = `h${level}` as const
    return React.createElement(Tag, { key, className: `sg-md-h${level}`, dangerouslySetInnerHTML: { __html: renderInline(m[2]) } })
  }
  if (b.type === "blockquote") {
    return (
      <blockquote key={key} className="sg-md-blockquote">
        <div dangerouslySetInnerHTML={{ __html: b.content.map(renderInline).join("<br/>") }} />
      </blockquote>
    )
  }
  if (b.type === "table") {
    const rows = b.content.map((r) => r.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()))
    const [headRow, sepRow, ...bodyRows] = rows
    if (!sepRow || !/^[-:]+/.test(sepRow[0] ?? "")) {
      return <pre key={key} className="sg-md-pre">{b.content.join("\n")}</pre>
    }
    return (
      <div key={key} className="sg-md-table-wrap">
        <table className="sg-md-table">
          <thead>
            <tr>{headRow.map((c, i) => <th key={i} dangerouslySetInnerHTML={{ __html: renderInline(c) }} />)}</tr>
          </thead>
          <tbody>
            {bodyRows.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => <td key={ci} dangerouslySetInnerHTML={{ __html: renderInline(c) }} />)}</tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  if (b.type === "list") return renderList(b.content, key)
  return <p key={key} className="sg-md-p" dangerouslySetInnerHTML={{ __html: b.content.map(renderInline).join("<br/>") }} />
}

// Group blocks into sections, each begun by a top-level (H1/H2) heading, so the
// `sectioned` renderer can wrap each in its own card. Blocks before the first such
// heading form a leading section. Opt-in — the default flat render is unchanged.
function sectionBlocks(blocks: Block[]): Block[][] {
  const groups: Block[][] = []
  let cur: Block[] = []
  for (const b of blocks) {
    const startsSection = b.type === "heading" && /^#{1,2}\s/.test(b.content[0] ?? "")
    if (startsSection && cur.length) { groups.push(cur); cur = [] }
    cur.push(b)
  }
  if (cur.length) groups.push(cur)
  return groups
}

export function GuideMarkdown({ markdown, theme, sectioned = false }: { markdown: string; theme?: GuideTheme; sectioned?: boolean }) {
  const blocks = blocksOf(markdown)
  const vars: React.CSSProperties = {
    // Neutral fallbacks; each app overrides via `theme`.
    ["--sg-text" as string]: theme?.text ?? "#2a2724",
    ["--sg-text-muted" as string]: theme?.textMuted ?? "#6b665f",
    ["--sg-heading" as string]: theme?.heading ?? "#1a1815",
    ["--sg-heading-font" as string]: theme?.headingFont ?? "'Space Grotesk', sans-serif",
    ["--sg-accent" as string]: theme?.accent ?? "#b8531f",
    ["--sg-line" as string]: theme?.line ?? "#e6e2d8",
    ["--sg-card-bg" as string]: theme?.cardBg ?? "#faf9f5",
    ["--sg-quote-bg" as string]: theme?.quoteBg ?? "#fdf3ec",
    ["--sg-quote-border" as string]: theme?.quoteBorder ?? "#e0a878",
    ["--sg-code-bg" as string]: theme?.codeBg ?? "#f0ede5",
  }
  return (
    <article className="sg-md" style={vars}>
      <style>{`
        .sg-md { color: var(--sg-text); line-height: 1.6; font-size: 14px; max-width: 820px; }
        .sg-md-h1 { font-size: 27px; font-weight: 700; letter-spacing: -0.02em; margin: 0 0 18px; color: var(--sg-heading); font-family: var(--sg-heading-font); }
        .sg-md-h2 { font-size: 19px; font-weight: 700; letter-spacing: -0.01em; margin: 30px 0 10px; color: var(--sg-heading); font-family: var(--sg-heading-font); }
        .sg-md-h3 { font-size: 15.5px; font-weight: 700; margin: 20px 0 8px; color: var(--sg-heading); }
        .sg-md-p { margin: 0 0 12px; color: var(--sg-text-muted); }
        .sg-md-blockquote { border-left: 3px solid var(--sg-quote-border); background: var(--sg-quote-bg); padding: 10px 14px; margin: 14px 0; border-radius: 6px; font-size: 13.5px; color: var(--sg-text); }
        .sg-md-hr { border: 0; border-top: 1px solid var(--sg-line); margin: 28px 0; }
        .sg-md-ol, .sg-md-ul { margin: 0 0 14px 22px; padding: 0; color: var(--sg-text-muted); }
        .sg-md-ol > li, .sg-md-ul > li { margin: 5px 0; }
        .sg-md-ul-nested { margin: 5px 0 6px 18px; }
        .sg-md-code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12.5px; background: var(--sg-code-bg); border: 1px solid var(--sg-line); border-radius: 4px; padding: 1px 5px; color: var(--sg-heading); }
        .sg-md-link { color: var(--sg-accent); text-decoration: underline; text-underline-offset: 2px; }
        .sg-md-link:hover { opacity: 0.8; }
        .sg-md-table-wrap { overflow-x: auto; margin: 14px 0; }
        .sg-md-table { border-collapse: collapse; width: 100%; font-size: 13px; }
        .sg-md-table th, .sg-md-table td { border: 1px solid var(--sg-line); padding: 8px 12px; text-align: left; vertical-align: top; }
        .sg-md-table th { background: var(--sg-card-bg); font-weight: 600; color: var(--sg-heading); }
        .sg-md strong { color: var(--sg-heading); font-weight: 600; }
        .sg-md-pre { white-space: pre-wrap; background: var(--sg-card-bg); padding: 10px; border-radius: 6px; font-size: 12px; }
        /* Sectioned mode: each H1/H2 section reads as its own card against the page canvas. */
        .sg-md-section { background: var(--sg-card-bg); border: 1px solid var(--sg-line); border-radius: 14px; padding: 18px 22px; margin: 0 0 16px; }
        .sg-md-section:last-child { margin-bottom: 0; }
        .sg-md-section > :first-child { margin-top: 0; }
        .sg-md-section > :last-child { margin-bottom: 0; }
      `}</style>
      {sectioned
        ? sectionBlocks(blocks).map((group, gi) => (
            <section key={gi} className="sg-md-section">
              {group.map((b, bi) => renderBlock(b, bi))}
            </section>
          ))
        : blocks.map((b, i) => renderBlock(b, i))}
    </article>
  )
}
